import React, { useEffect, useState } from 'react'

import { Autocomplete, TextField, Tooltip, Chip, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useDebounce } from 'use-debounce'

import AppLayout from '../../components/Layouts/AppLayout'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import ServerTable from '../../components/ServerTable'
import axios from '../../lib/axios'
import { useAuth } from '../../hooks/auth'
import { useStateContext } from '../../context/ContextProvider'
import { handleAxiosError, isValid } from '../../helpers/helper'
import { buildUrl } from '../../helpers/apiHelper'

const searchInputSx = {
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    background: '#F5F5F5',
    borderRadius: '6px',
}

const Routings = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuth({ middleware: 'guest' })
    const { config, choosesite, setChoosesite, pusher } = useStateContext()

    const [isLoading, setIsLoading] = useState(false)
    const [routings, setRoutings] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [random, setRandom] = useState(null)
    const [model, setModel] = useState({
        routing_code: true,
        routing_name: true,
        site_name: true,
        description: true,
        operations_count: true,
        status: true,
        actions: true
    })

    /* filter options */
    const [statusOptions, setStatusOptions] = useState([])

    /* filters - restored from localStorage so they survive leaving/reopening this page */
    const [filterCode, setFilterCode] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.routing_code || ''
        }
        return ''
    })
    const [filterName, setFilterName] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.routing_name || ''
        }
        return ''
    })
    const [filterStatus, setFilterStatus] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.routing_status || ''
        }
        return ''
    })

    const [debouncedCode] = useDebounce(filterCode, 500)
    const [debouncedName] = useDebounce(filterName, 500)

    useEffect(() => {
        getTableView()
    }, [])

    /* site follows the global site chosen in the navigation, same as Forecasts/Work Centers */
    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    useEffect(() => {
        getStatusOptions()
    }, [])

    useEffect(() => {
        if (isValid(choosesite)) {
            getRoutings()
        }
    }, [debouncedCode, debouncedName, choosesite, filterStatus, page, pageSize, random])

    /* persist filters to localStorage so they survive leaving/reopening this page */
    const createFilterArray = (code, name, status) => {
        if (localStorage.getItem('filters') === null) {
            localStorage.setItem('filters', JSON.stringify([{}]))
        }

        const filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].routing_code = code
        filters[0].routing_name = name
        filters[0].routing_status = status

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    useEffect(() => {
        createFilterArray(filterCode, filterName, filterStatus)
    }, [filterCode, filterName, filterStatus])

    /* real-time updates: any create/update/delete of a routing refreshes the list for everyone */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')

        const channelCreated = pusher.subscribe(`${clientId}-routing-created`)
        const channelUpdated = pusher.subscribe(`${clientId}-routing-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-routing-deleted`)

        channelCreated.bind(`${clientId}-routing-created-event`, data => {
            setRandom(Math.random())
        })

        channelUpdated.bind(`${clientId}-routing-updated-event`, data => {
            setRandom(Math.random())
        })

        channelDeleted.bind(`${clientId}-routing-deleted-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-routing-created`)
            pusher.unsubscribe(`${clientId}-routing-updated`)
            pusher.unsubscribe(`${clientId}-routing-deleted`)
        })
    }, [])

    const getTableView = async () => {
        await axios.get(`/api/get-table-view?view_name=routings_table&user_id=${user?.id}`, config)
            .then(res => {
                const savedModel = res.data
                if (Object.keys(savedModel?.success?.data ?? {}).length > 0) {
                    setModel(savedModel.success.data)
                }
            })
            .catch(({ response }) => {
                console.error(response)
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/routing-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getRoutings = async () => {
        setIsLoading(true)

        const url = buildUrl(
            '/api/routings',
            {
                where: {
                    routing_code: debouncedCode,
                    routing_name: debouncedName,
                    site_id: choosesite,
                    status: filterStatus,
                    page: page + 1,
                    page_size: pageSize
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                setRoutings(res.data?.data ?? [])
                setTotal(res.data?.total ?? 0)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleDelete = async (row) => {
        const isConfirm = await Swal.fire({
            title: `${t('title_delete--long', { x: t('routing').toLocaleLowerCase(), y: row.routing_name })}`,
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) return

        await axios.delete(`/api/routings/${row.id}`, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })
            getRoutings()
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        })
    }

    const statusLabel = (value) => statusOptions.find(o => o.value === value)?.label ?? value

    const columns = [
        { field: 'routing_code', headerName: t('code'), flex: 0.7, minWidth: 130 },
        { field: 'routing_name', headerName: t('name'), flex: 1, minWidth: 180 },
        { field: 'site_name', headerName: t('site'), flex: 0.8, minWidth: 150 },
        { field: 'description', headerName: t('description'), flex: 1.2, minWidth: 180 },
        {
            field: 'operations_count',
            headerName: t('no_of_operations'),
            flex: 0.7,
            minWidth: 150,
            valueGetter: ({ row }) => row.routing_operations?.length ?? 0
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 0.6,
            minWidth: 120,
            renderCell: (params) => (
                <Chip
                    label={statusLabel(params.value)}
                    size='small'
                    sx={{
                        backgroundColor: String(params.value).toLowerCase() === 'active' ? '#E3F8EF' : '#F5F5F5',
                        color: String(params.value).toLowerCase() === 'active' ? '#00B78E' : '#88909C',
                        fontWeight: 600
                    }}
                />
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.6,
            minWidth: 130,
            renderCell: (params) => (
                <div className='flex gap-1'>
                    <Tooltip disableInteractive title={t('view')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}`)}><i className="fa-solid fa-eye"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('update')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}/edit`)}><i className="fa-solid fa-pencil"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => handleDelete(params.row)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-6 flex w-full items-start justify-between gap-5'>
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#EEEFF2]'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('routings')}</p>
                            <Tooltip disableInteractive title={t('create_routing')} placement='bottom'>
                                <div>
                                    <AddButton onClick={() => navigate('create')}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>

                        <div className='p-5 border-b border-[#EEEFF2]'>
                            <div className='flex-row flex justify-between items-end w-full gap-3'>
                                <div className='w-full'>
                                    <TextField
                                        fullWidth
                                        placeholder={t('search_by_routing_code')}
                                        value={filterCode}
                                        onChange={(e) => { setFilterCode(e.target.value); setPage(0) }}
                                        sx={searchInputSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon sx={{ color: 'rgba(0,0,0,.54)' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                                <div className='w-full'>
                                    <TextField
                                        fullWidth
                                        placeholder={t('search_by_routing_name')}
                                        value={filterName}
                                        onChange={(e) => { setFilterName(e.target.value); setPage(0) }}
                                        sx={searchInputSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon sx={{ color: 'rgba(0,0,0,.54)' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        options={statusOptions}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={statusOptions.find(o => o.value === filterStatus) || null}
                                        onChange={(e, value) => { setFilterStatus(value?.value || ''); setPage(0) }}
                                        sx={searchInputSx}
                                        renderInput={(params) => <TextField {...params} placeholder={t('search_by_status')} />}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='pt-3'>
                            <ServerTable
                                columns={columns}
                                rowsPerPageOptions={[10, 15, 30]}
                                items={routings}
                                total={total}
                                setPage={setPage}
                                setPageSize={setPageSize}
                                page={page}
                                pageSize={pageSize}
                                columnVisibilityModel={model}
                                setModel={setModel}
                                view='routings'
                            />
                        </div>
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default Routings
